import { z } from 'zod';
/**
 * Video call with a remote operator from the kiosk "Call staff" button (owner 20.10.2026,
 * project doc 07b). The call runs on Daily; Hub creates a private room per call and short-lived
 * tokens. No recording, no video or audio passes through Hub.
 *
 * Timeline of a call:
 *   ringing ──(operator answers)──▶ active ──(end / limit)──▶ ended
 *      │  30 s: alert on the therapist tablets of the location
 *      │  60 s: the kiosk also shows the studio WhatsApp (QR)
 *      └─ 3 min without an answer ──▶ missed
 */
/** No answer for this long → red alert on the therapist tablets. */
export const ASSIST_TABLETS_AFTER_MS = 30_000;
/** No answer for this long → the kiosk shows the studio WhatsApp number and QR. */
export const ASSIST_WHATSAPP_AFTER_MS = 60_000;
/** A call nobody answered is closed after this long. */
export const ASSIST_RING_MAX_MS = 3 * 60_000;
/** Owner 20.10.2026: a call lasts at most 15 minutes unless the operator extends it. */
export const ASSIST_CALL_LIMIT_MS = 15 * 60_000;
/** Warning before the end ("the call ends in 2 minutes"). */
export const ASSIST_WARN_BEFORE_MS = 2 * 60_000;
/** One "extend" adds this much. */
export const ASSIST_EXTEND_MS = 10 * 60_000;
/** Hard maximum of one call, extensions included. */
export const ASSIST_CALL_MAX_MS = 45 * 60_000;
/**
 * What the operator sees of the kiosk screen — only while the call is connected (owner 20.10.2026).
 * Safari on iPad cannot share the screen from a web page, so the kiosk sends its state instead.
 * Short texts only; never the phone number, e-mail or health answers.
 */
export const AssistMirrorSchema = z
    .object({
    /** Kiosk step id, e.g. `time`, `details`, `pay`. */
    step: z.string().regex(/^[a-z_]{1,30}$/),
    /** What the customer is doing: booking, check-in, gift… */
    mode: z
        .string()
        .regex(/^[a-z_]{1,20}$/)
        .optional(),
    locale: z.string().regex(/^[a-z]{2}$/),
    service: z.string().max(80).optional(),
    /** Local date YYYY-MM-DD and time HH:MM of the chosen slot. */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    time: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .optional(),
    staff: z.string().max(60).optional(),
    extras: z.number().int().min(0).max(20).optional(),
    /** Error code on the screen, e.g. SLOT_TAKEN. */
    notice: z
        .string()
        .regex(/^[A-Z_]{1,40}$/)
        .optional(),
})
    // Unknown fields are rejected, so a phone or e-mail can never slip into the mirror.
    .strict();
export const AssistCallRequestSchema = z.object({
    /** Screen the customer was on when they tapped "Call staff" (no personal data). */
    screen: z.string().regex(/^[a-z_]{1,30}$/),
    locale: z.string().regex(/^[a-z]{2}$/),
});
export const OperatorLoginSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(120),
    pin: z.string().regex(/^\d{4,8}$/),
});
/** Stage of a ringing call after `ms` without an answer. */
export function assistStage(ms, operatorsOnline) {
    if (!operatorsOnline || ms >= ASSIST_WHATSAPP_AFTER_MS)
        return 'whatsapp';
    if (ms >= ASSIST_TABLETS_AFTER_MS)
        return 'tablets';
    return 'calling';
}
//# sourceMappingURL=assist.js.map